import { useEffect, useState } from 'react';
import { theme } from '../theme.js';
import { getPushState, enablePush, disablePush, sendTestPush, type PushState } from '../lib/push.js';
import { emitError } from '../lib/toastBus.js';
import { useMe, useSetEmailNotifications, useSetShiftReminder, useSetMonthlyReportEmail } from '../lib/queries.js';
import { useAccess } from '../context/AuthContext.js';
import type { ShiftReminderHours } from '@keetcheck/shared';

const HINTS: Partial<Record<PushState, string>> = {
  'unsupported':    "This browser can't show notifications.",
  'ios-install':    'On iPhone, add MusterTap to your Home Screen first (see above), then open it from there.',
  'not-configured': "Notifications aren't set up on the server yet.",
  'denied':         'Notifications are blocked for this site. Allow them in your browser or phone settings, then come back.',
  'off':            'Get a ping on this device when something needs you.',
  'on':             'This device gets notifications.',
};

/** Profile card: turn push notifications on/off for this device, and test;
 *  and whether to get them by email when no device has push on. */
export function NotificationsCard() {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [tested, setTested] = useState(false);
  const { data: me } = useMe();
  const emailMut = useSetEmailNotifications();
  const reminderMut = useSetShiftReminder();
  const reportMut = useSetMonthlyReportEmail();
  const { isAdmin } = useAccess();

  useEffect(() => {
    let alive = true;
    getPushState()
      .then((s) => { if (alive) setState(s); })
      .catch(() => { if (alive) setState('unsupported'); });
    return () => { alive = false; };
  }, []);

  async function run(fn: () => Promise<PushState>) {
    setBusy(true);
    try {
      setState(await fn());
    } catch (e) {
      emitError(e instanceof Error ? e.message : 'Could not change notifications');
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    try {
      await sendTestPush();
      setTested(true);
    } catch (e) {
      emitError(e instanceof Error ? e.message : 'Test notification failed');
    } finally {
      setBusy(false);
    }
  }

  if (state === null) return null;
  const canToggle = state === 'on' || state === 'off';

  return (
    <div style={{
      margin: '12px 16px', padding: '12px 14px', borderRadius: 12,
      background: theme.surface, border: `1px solid ${theme.border}`,
      fontFamily: theme.font,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>Notifications</div>
          <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.4 }}>{HINTS[state]}</div>
        </div>
        {canToggle && (
          <Switch on={state === 'on'} label="Notifications on this device" busy={busy}
            onClick={() => void run(state === 'on' ? disablePush : enablePush)} />
        )}
      </div>
      {state === 'on' && (
        <button onClick={() => void test()} disabled={busy} style={{
          marginTop: 10, padding: '7px 12px', borderRadius: 8,
          border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
          fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
        }}>
          {tested ? 'Sent — send another' : 'Send a test notification'}
        </button>
      )}
      {me && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>Email me instead</div>
            <div style={{ fontSize: 12, color: me.emailSuppressed ? theme.status.danger.fg : theme.textMuted, lineHeight: 1.4 }}>
              {me.emailSuppressed
                ? `Emails to ${me.email} ${me.emailSuppressed === 'bounce' ? 'bounced' : 'were marked as spam'}, so we've stopped sending them. Ask your unit admin to update your email.`
                : 'When none of your devices has notifications on, they come by email.'}
            </div>
          </div>
          <Switch on={me.emailNotifications} label="Email notifications" busy={emailMut.isPending}
            onClick={() => emailMut.mutate(!me.emailNotifications)} />
        </div>
      )}
      {me && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>Remind me before my shifts</div>
            <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.4 }}>
              Including covers you’ve taken. If that’s in your quiet hours, it comes just before they start.
            </div>
          </div>
          <select value={me.shiftReminderHours} disabled={reminderMut.isPending} aria-label="Shift reminder"
            onChange={(e) => reminderMut.mutate(Number(e.target.value) as ShiftReminderHours)}
            style={{ padding: '7px 8px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text, fontSize: 13, fontFamily: theme.font, flexShrink: 0 }}>
            <option value={0}>Off</option>
            <option value={1}>1 hour before</option>
            <option value={3}>3 hours before</option>
            <option value={12}>12 hours before</option>
            <option value={24}>24 hours before</option>
          </select>
        </div>
      )}
      {me && isAdmin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>Monthly unit report</div>
            <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.4 }}>Last month’s report by email on the 1st. Any period is in Admin → Reports.</div>
          </div>
          <Switch on={me.monthlyReportEmail} label="Monthly report email" busy={reportMut.isPending}
            onClick={() => reportMut.mutate(!me.monthlyReportEmail)} />
        </div>
      )}
    </div>
  );
}

export function Switch({ on, label, busy, onClick }: { on: boolean; label: string; busy: boolean; onClick: () => void }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={busy}
      onClick={onClick}
      style={{
        width: 46, height: 28, borderRadius: 14, border: 'none', padding: 3,
        background: on ? theme.accent : theme.border,
        cursor: busy ? 'default' : 'pointer', flexShrink: 0,
        display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start',
        opacity: busy ? 0.6 : 1, transition: 'background 0.15s',
      }}
    >
      <span style={{ width: 22, height: 22, borderRadius: 11, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
    </button>
  );
}
